import { useEffect, useState } from 'react';

/**
 * useIngressOrigin
 * ----------------
 * Host-aware ingress detection + lightweight local cache.
 *
 * Behavior:
 *  1. On mount (the React analog of document/DOMContentLoaded init), parse
 *     `?src=` from `window.location.search`.
 *  2. Accept only known origins: 'instagram' | 'linkedin' | 'academy' | 'dila'.
 *  3. If a known origin is detected, persist it to
 *     `localStorage.sov_ingress_origin` and return it.
 *  4. If no valid `src` param exists, fall back to the cached value (so the
 *     priority highlight survives a later refresh / direct visit at $0 cloud
 *     server cost).
 *  5. If neither exists, return `null` (UI defaults to balanced static state).
 *
 * All localStorage access is wrapped in try/catch to remain safe under
 * privacy-mode / iframe / SSR contexts.
 */
const VALID_ORIGINS = new Set(['instagram', 'linkedin', 'academy', 'dila']);
const STORAGE_KEY = 'sov_ingress_origin';

const readCache = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const cached = window.localStorage.getItem(STORAGE_KEY);
    return cached && VALID_ORIGINS.has(cached) ? cached : null;
  } catch (err) {
    return null;
  }
};

const writeCache = (value) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (err) {
    /* swallow — best-effort cache only */
  }
};

const readSearchParam = () => {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const src = (params.get('src') || '').toLowerCase().trim();
    return VALID_ORIGINS.has(src) ? src : null;
  } catch (err) {
    return null;
  }
};

export default function useIngressOrigin() {
  const [origin, setOrigin] = useState(null);

  useEffect(() => {
    const fromParam = readSearchParam();
    if (fromParam) {
      writeCache(fromParam);
      setOrigin(fromParam);
      return;
    }
    const fromCache = readCache();
    if (fromCache) {
      setOrigin(fromCache);
    }
  }, []);

  return origin;
}
